import { ApiError } from '@/lib/api/client';

/**
 * Endpoints that answer with a file.
 *
 * A handful of them — `giftcards/create`, `inventory/export/{businessId}` —
 * return a `FileResult` on success but fall back to `OkError(...)` on failure,
 * and both come back as HTTP 200. So a client reading the response as a blob
 * has to look at what it actually got before offering it as a download; the
 * original downloaded a `.csv` containing an error object and called it done.
 */

/** Hand a string to the browser as a file. */
export function saveAsFile(
  text: string,
  filename: string,
  type = 'text/plain;charset=utf-8'
): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Without this the blob is held for the life of the document.
  URL.revokeObjectURL(url);
}

/**
 * Read a blob response, refuse it if it is really an error envelope, and save
 * it. Returns the text, for callers that want to say something about it.
 *
 * `request` must have been made with `responseType: 'blob'`.
 */
export async function downloadResponse(
  request: Promise<{ data: unknown }>,
  filename: string,
  type?: string
): Promise<string> {
  const response = await request;
  const text = await (response.data as Blob).text();

  // A JSON body here means the envelope, which means it failed.
  if (text.trimStart().startsWith('{')) {
    const body = JSON.parse(text) as { error?: string; message?: string };
    throw new ApiError(
      body.error || body.message || 'The request was not accepted.'
    );
  }

  saveAsFile(text, filename, type);
  return text;
}
